// Copyright (c) 2017 Comsearch, Inc., All rights reserved

import { logOut } from 'helpers/auth'

/* global API_ROOT API_PORT */
const apiPath = (
  API_PORT !== undefined
    // if API port is defined, use same domain but different port.
    ? `//${window.location.hostname}:${API_PORT}`
    // if API root is defined, use that
    // otherwise default it to this domain at this port.
    : API_ROOT || `//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}`
)

const headers = {
  Accept: 'application/json',
  'Cache-Control': 'no-cache',
  'Content-Type': 'application/json',
  Pragma: 'no-cache',
}

// assigns the jwt token to the API Headers
export const updateHeaderToken = token => {
  headers.Authorization = token
}

export const configDELETE = {
  headers,
  method: 'DELETE',
}

export const configGET = {
  headers,
  method: 'GET',
}

export const configPATCH = {
  headers,
  method: 'PATCH',
}

export const configPOST = {
  headers,
  method: 'POST',
}

export const configPUT = {
  headers,
  method: 'PUT',
}

// if an error is caught, this responds
// with the proper error message
// instead of a promise
export const fetchResponse = async res => {
  const responseIsJson = res.headers.get('content-type').toLowerCase().includes('json')
  const responseClone = res.clone()

  // request was successful
  if (res.ok && responseIsJson) {
    return res.json()
  }

  // 401 response - user was not authorized
  // 403 response - user is forbidden from the action they just tried.
  // a 403 should never happen in the UI so if the UI gets this response status,
  // it means the user's trying to do something sneaky. log them out!
  if (res.status === 401 || res.status === 403) {
    logOut()
    // force wait for 5 seconds while browser is refreshing
    await (new Promise((resolve) => {
      setTimeout(() => resolve(), 5000)
    }))
  }

  let json = null

  // verify response was JSON type
  if (responseIsJson) {
    json = await responseClone.json()

  // else check to see if it's html/XML and we can extract a message.
  } else {
    const responseText = await responseClone.text()
    // if it has an h1, use that, if it has a title, use that, else just put the entire response text as the response.
    const message = (
      responseText.includes('<h1>') && responseText.split(/<h1>/)?.[1]?.split(/<\/h1>/)?.[0] ||
      responseText.includes('<title>') && responseText.split(/<title>/)?.[1]?.split(/<\/title>/)?.[0] ||
      responseText
    )

    json = {
      status: res.status,
      message,
    }
  }

  // There are a few different ways we're capturing errors from the API.
  // The standard one is that we have an error message from the response (json.message)
  // The other is that the proxy might not be happy (res.statusText)
  let err = json?.message || res.statusText

  // Next, we can have one to many errors in an errors array.
  // This errors array can come from two different places:
  // Spring validation and a custom backend validator

  // The Spring Validation is being phased out but for the time being there are
  // still a few places which can return this error type.
  // To handle that scenario, we check to see if json.errors[].defaultMessage is defined.
  // If it is, then we have Spring errors. Roll those up into a string to display to the user.
  if (json?.errors?.length && json.errors[0].defaultMessage !== undefined) {
    const l = json.errors.length
    err = `Found ${l} Error${l > 1 ? 's' : ''}: `
    json.errors.map(d => { err += `${d.defaultMessage}. ` })
  }

  // If we have json.errors but no 'defaultMessage', that means we're consuming
  // errors from the backend custom validator. These errors should be displayed with hierarchy
  // to the user so that they can easily see what is wrong and where.
  // However, with these types of errors, it is up to the caller to decide how it
  // should be managed. (In place of rolling up the messages like we do above.)

  // create an Error, give it the res/json
  // and throw it to the caller's exception handler
  const error = new Error(err)
  throw Object.assign(error, { res, json })
}

export const fetchWrapper = async (url, config = configGET, body) => {
  const r = await fetch(`${apiPath}/api${url}`, {
    ...config,
    body,
  })
  return fetchResponse(r)
}

export const deleteWrapper = url => (
  fetchWrapper(
    url,
    configDELETE,
  )
)

export const patchWrapper = (url, body = '') => (
  fetchWrapper(
    url,
    configPATCH,
    JSON.stringify(body),
  )
)

export const postWrapper = (url, body = '') => (
  fetchWrapper(
    url,
    configPOST,
    JSON.stringify(body),
  )
)

// this posts FormData and not JSON.
export const postFormDataWrapper = (url, formData) => (
  fetchWrapper(
    url,
    {
      method: 'POST',
      headers: {
        // since we're not using the generic 'headers' object, we need to
        // pull in the token every time we want to make this call
        Authorization: headers.Authorization,
      },
    },
    formData,
  )
)

export const putWrapper = (url, body) => (
  fetchWrapper(
    url,
    configPUT,
    JSON.stringify(body),
  )
)

// creates an array of pagination parameters for an API request
export const makePaginationParams = ({ page = 0, size }) => {
  const params = [{
    field: 'page',
    value: page,
  }]

  if (size) {
    params.push({
      field: 'size',
      value: size,
    })
  }

  return params
}

// NOTE - The API will expect arrays in two different ways:
// ?foo=[1,2,3]
// and
// ?foo=1,2,3
// This depends on how that param is configured in the back end!
// If you need to convert an array and pass it as a comma spaced string of values,
// you can arr.join(',')
export const makeQueryString = s => {
  const t = typeof s !== 'string' ? JSON.stringify(s) : s
  return encodeURIComponent(t)
}

// when using the 2d search array in query params,
// use this method to quickly build the search criteria.
export const makeSearchObject = (field = null, value = null, operator = 'LIKE') => (
  value !== null && field !== null
    ? ({
      field,
      operator,
      value,
    })
    : null
)

// builds a query string from the array of flags passed to it.
// this also adds an 'expires' query to prevent any sort of caching.
// this is because IE11 gets real clingy with the original responses
// despite the fetch headers telling it explicitly NOT to.
// eg of passing this a 2d "search" array:
// a = [{
//   field: 'search',
//   value: [{
//     field: 'companyId',
//     operator: 'EQ',
//     value: 3,
//   }]
// }]
export const assembleQuery = (a = []) => {
  a.push({
    field: 'expires',
    value: Date.now(),
  })

  return a.reduce((q, d, i) => {
    // separated for readability
    const s = !i ? '?' : '&'
    const f = d.field
    const v = makeQueryString(d.value)
    q += `${s}${f}=${v}`
    return q
  }, '')
}

export const createMultiFormData = formValues => (
  Object.keys(formValues).reduce((a, d, i) => {
    // if there are multiple files, append em
    if (d === 'files') {
      formValues[d].map(file => a.append(d, file))

    // if there is a single file, it will be held in an array through redux-form.
    // we need to strip the array when appending this file to the formData.
    } else if (d === 'file') {
      a.append(d, formValues[d][0])
    } else {
      a.append(d, formValues[d])
    }
    return a
  }, new FormData())
)
